import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Tag,
  Wrap,
  WrapItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Textarea,
  Spinner,
  Button,
} from "@chakra-ui/react";
import colors from "../../../utils/colors";
import {
  useSystemClasses,
  useSymptomDescriptions,
  useAddReviewOfSystemsComplaint,
  useGetPatientDetails,
} from "../../../apis/patients";
import customToast from "../../../components/Toast";
import { useLocation } from "react-router-dom";

interface ReviewOfSystemProps {
  appointmentId: string;
}

const ReviewOfSystemsCard: React.FC<ReviewOfSystemProps> = ({
  appointmentId,
}) => {
  const location = useLocation();
  const patientIdFromRoute = React.useMemo(
    () => location?.pathname?.split("/")?.[2] ?? "",
    [location?.pathname]
  );
  const { data: patientData } = useGetPatientDetails({
    patientId: patientIdFromRoute,
  });

  //console.log(patientData.data.gender);
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [openSystem, setOpenSystem] = useState<string | null>(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState<{
    [key: string]: string[];
  }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [note, setNote] = useState<string>("");
  const symptomsPerPage = 5;

  const { data: systemsData = [], isLoading: loadingSystems } =
    useSystemClasses();
  const { data: symptomResponse, isLoading: loadingSymptoms } =
    useSymptomDescriptions(selectedSystem);
  const { mutate: addReviewOfSystemsComplaint, isPending } =
    useAddReviewOfSystemsComplaint();
  const symptomDescriptions: string[] = Array.isArray(symptomResponse?.data)
    ? symptomResponse.data
    : [];

  const hasSelectedSymptoms = Object.values(selectedSymptoms).some(
    (symptoms) => symptoms.length > 0
  );
  const noteIsEmpty = note.trim() === "";
  const isInvalid = !hasSelectedSymptoms && noteIsEmpty;

  const handleSelectSymptom = (system: string, symptom: string) => {
    setSelectedSymptoms((prev) => {
      const symptoms = prev[system] || [];

      return {
        ...prev,
        [system]: symptoms.includes(symptom)
          ? symptoms.filter((s) => s !== symptom)
          : [...symptoms, symptom],
      };
    });
  };

  // Normalize gender (supports "Male"/"Female" or "M"/"F")
  const genderRaw = String(patientData?.data?.gender ?? "")
    .trim()
    .toLowerCase();
  const isMale = genderRaw === "male" || genderRaw === "m";
  const isFemale = genderRaw === "female" || genderRaw === "f";

  // Filter the systems list to show only the correct urogenital section
  const filteredSystems = React.useMemo(() => {
    // Preserve original order; include both when gender is unknown
    return systemsData.filter((s: string) => {
      if (s === "Male Urogenital") return isMale || (!isMale && !isFemale);
      if (s === "Female Urogenital") return isFemale || (!isMale && !isFemale);
      return true;
    });
  }, [systemsData, isMale, isFemale]);

  // If an open system gets filtered out (e.g., switching gender), close it
  React.useEffect(() => {
    if (openSystem && !filteredSystems.includes(openSystem)) {
      setOpenSystem(null);
      setSelectedSystem(null);
    }
  }, [filteredSystems, openSystem, setOpenSystem, setSelectedSystem]);

  const indexOfLastSymptom = currentPage * symptomsPerPage;
  const indexOfFirstSymptom = indexOfLastSymptom - symptomsPerPage;
  const currentSymptoms = symptomDescriptions.slice(
    indexOfFirstSymptom,
    indexOfLastSymptom
  );
  const totalPages = Math.ceil(symptomDescriptions.length / symptomsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (!hasSelectedSymptoms && noteIsEmpty) {
      customToast(
        "Please select at least one symptom or enter a note.",
        "error"
      );
      return;
    }

    const formattedData = Object.entries(selectedSymptoms).flatMap(
      ([system, symptoms]) =>
        (Array.isArray(symptoms) ? symptoms : []).map((symptom) => ({
          complaint: symptom,
          bodyPart: system,
          cond: "",
          note: note.trim(),
        }))
    );

    let payloadData = formattedData;
    if (!hasSelectedSymptoms && !noteIsEmpty) {
      payloadData = [
        {
          complaint: "",
          bodyPart: "",
          cond: "",
          note: note.trim(),
        },
      ];
    }

    addReviewOfSystemsComplaint(
      { appointmentId, data: payloadData },
      {
        onSuccess: () => {
          customToast("Review of Systems submitted successfully!", "success");
          setSelectedSymptoms({});
          setNote("");
        },
        onError: (error: any) => {
          customToast(
            `Error submitting: ${
              error.response?.data?.message || error.message
            }`,
            "error"
          );
        },
      }
    );
  };

  if (loadingSystems) {
    return <Spinner size="lg" color={colors.primary} />;
  }

  return (
    <Flex
      direction="column"
      backgroundColor="white"
      borderRadius="8px"
      boxShadow="1px 4px 15px 0px #3030301F"
      width="100%"
      padding="40px"
      gap="20px"
    >
      <Accordion allowToggle>
        {/* {systemsData.map((system) => ( */}

        {filteredSystems.map((system: string) => (
          <AccordionItem key={system} mt="20px">
            <AccordionButton
              onClick={() => {
                if (openSystem === system) {
                  setOpenSystem(null);
                  setSelectedSystem(null);
                } else {
                  setOpenSystem(system);
                  setSelectedSystem(system);
                  setCurrentPage(1);
                }
              }}
            >
              <Box flex="1" textAlign="left">
                <Text
                  as="div"
                  fontFamily="lato"
                  fontSize="14px"
                  fontWeight={700}
                  color={colors.dark}
                  display="flex"
                  alignItems="center"
                >
                  {system}{" "}
                  {loadingSymptoms && selectedSystem === system && (
                    <Spinner size="xs" ml="5px" />
                  )}
                </Text>
                {selectedSymptoms[system]?.length > 0 && (
                  <Text color={colors.secondaryBlack} fontSize="14px" mt="2px">
                    {selectedSymptoms[system].join(", ")}
                  </Text>
                )}
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              {loadingSymptoms ? (
                <Spinner size="sm" color={colors.primary} />
              ) : currentSymptoms.length > 0 ? (
                <>
                  <Wrap>
                    {currentSymptoms.map((symptom) => (
                      <WrapItem key={symptom}>
                        <Tag
                          size="lg"
                          variant={
                            selectedSymptoms[system]?.includes(symptom)
                              ? "solid"
                              : "outline"
                          }
                          colorScheme="green"
                          cursor="pointer"
                          onClick={() => handleSelectSymptom(system, symptom)}
                          whiteSpace={"normal"}
                          py={"7px"}
                        >
                          <>{symptom}</>
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>

                  {totalPages > 1 && (
                    <Flex justifyContent="space-between" mt="10px">
                      <Button
                        size="sm"
                        onClick={handlePrevPage}
                        isDisabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Text fontSize="14px" fontWeight="bold">
                        Page {currentPage} of {totalPages}
                      </Text>
                      <Button
                        size="sm"
                        onClick={handleNextPage}
                        isDisabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </Flex>
                  )}
                </>
              ) : (
                <Text>No symptoms found for this system.</Text>
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <Box mt="20px">
        <Text
          fontFamily="lato"
          fontSize="14px"
          fontWeight={600}
          mb="12px"
          color={colors.dark}
        >
          Note
        </Text>
        <Textarea
          focusBorderColor={colors.primary}
          placeholder="Enter any additional notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Box>
      <Button
        type="submit"
        mt="20px"
        colorScheme="green"
        isLoading={isPending}
        onClick={handleSubmit}
        disabled={isPending || isInvalid}
      >
        Submit Complaints
      </Button>
    </Flex>
  );
};

export default ReviewOfSystemsCard;
